import { FormEvent, useState } from "react";

const FORM_URL = "https://readdy.ai/api/form/dafb0arep053ijub1n0g";

type Status = "idle" | "submitting" | "success" | "error";

export default function SubmitSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get("website_alt") as string | null)?.trim() || "";

    setStatus("submitting");
    setFormError("");

    // 反垃圾：蜜罐被填写则静默当作成功，不真正提交
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    formData.delete("website_alt");
    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, String(value));
    });

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = /spam/i.test(serverMsg);

      if (!response.ok || !parsed || parsed.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(serverMsg && !isSpam ? serverMsg : "提交失败，请稍后再试或直接邮箱联系我们。");
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络似乎开小差了，请稍后再试一次。");
    }
  };

  return (
    <section id="submit" className="w-full bg-background-50 py-16 md:py-20">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 overflow-hidden rounded-3xl border border-background-200 bg-background-100 lg:grid-cols-2">
          <div className="relative flex flex-col justify-center overflow-hidden p-8 md:p-12">
            <div className="absolute -left-10 -top-10 h-44 w-44 rounded-full bg-accent-100"></div>
            <div className="relative">
              <p className="text-sm font-medium text-accent-500 whitespace-nowrap">SUBMIT YOUR PICK</p>
              <h2 className="font-display mt-2 text-2xl text-foreground-900 md:text-4xl">推荐你心中的那口好瓜</h2>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-foreground-600 md:text-base">
                吃到一口好评如潮、值得被更多人看见的内容？把它推荐给我们吧。
                审核通过后，你的推荐会结合网友评分进入对应高分合集，让口碑继续流传。
              </p>
              <ul className="mt-6 space-y-3 text-sm text-foreground-700">
                {[
                  { icon: "ri-checkbox-circle-line", t: "评审通过即入选，注明你的推荐昵称" },
                  { icon: "ri-time-line", t: "一般 3 个工作日内给出回音" },
                  { icon: "ri-mail-line", t: "合作与反馈：hi@chigua.jingxuan" },
                ].map((it) => (
                  <li key={it.t} className="flex items-center gap-2">
                    <i className={`${it.icon} text-lg text-primary-500`}></i>
                    <span className="whitespace-nowrap">{it.t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <form
            data-readdy-form="chigua-submit"
            onSubmit={handleSubmit}
            className="flex flex-col gap-4 bg-background-50 p-8 md:p-12"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="sb-name" className="text-xs font-medium text-foreground-600">
                  你的昵称
                </label>
                <input
                  id="sb-name"
                  name="name"
                  type="text"
                  required
                  placeholder="例如：老瓜农·阿哲"
                  className="h-11 w-full rounded-lg border border-background-200 bg-background-50 px-3 text-sm text-foreground-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="sb-email" className="text-xs font-medium text-foreground-600">
                  邮箱（用于接收审核结果）
                </label>
                <input
                  id="sb-email"
                  name="email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="h-11 w-full rounded-lg border border-background-200 bg-background-50 px-3 text-sm text-foreground-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="sb-title" className="text-xs font-medium text-foreground-600">
                推荐的好瓜标题 / 主题
              </label>
              <input
                id="sb-title"
                name="title"
                type="text"
                required
                placeholder="一句话说出你想推荐的好瓜"
                className="h-11 w-full rounded-lg border border-background-200 bg-background-50 px-3 text-sm text-foreground-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="sb-content" className="text-xs font-medium text-foreground-600">
                推荐理由
              </label>
              <textarea
                id="sb-content"
                name="content"
                required
                maxLength={500}
                rows={4}
                placeholder="为什么值得一看？哪些网友反馈让你印象深刻？（限 500 字）"
                className="w-full resize-none rounded-lg border border-background-200 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              ></textarea>
            </div>

            {/* 反垃圾蜜罐字段，普通样式隐藏 */}
            <input id="websiteField" name="website_alt" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />

            {status === "error" && (
              <div className="flex items-start gap-2 rounded-lg bg-accent-50 p-3 text-sm text-accent-700" role="alert">
                <i className="ri-error-warning-line mt-0.5"></i>
                <span>{formError}</span>
              </div>
            )}
            {status === "success" && (
              <div className="flex items-start gap-2 rounded-lg bg-primary-50 p-3 text-sm text-primary-700">
                <i className="ri-checkbox-circle-line mt-0.5"></i>
                <span>提交成功，感谢你分享好瓜！我们会尽快审核并在合适时与你联系。</span>
              </div>
            )}

            <button
              type="submit"
              disabled={status === "submitting"}
              className="mt-1 inline-flex h-12 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {status === "submitting" ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  提交中…
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line"></i>
                  提交推荐
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}